// src/encounter/lab-tests-list.tsx
// The Add lab popup's TESTS list (PROD-2640). Presentational only: the dialog owns the selection
// and the rules live in lib/lab-catalogue.ts.
//
// Design: canvas "Lab Panels and Tests", artboards 6-8 (AddLabAtomic / AddLabHouse /
// AddLabDuplicate) and 9 (EdgeStates). Anatomy, top to bottom:
//   · "TESTS (n/m)" (or "TESTS (3 new · 1 already ordered · 1 reflex)") in the field-label style;
//   · a bordered list box, 8px radius, one row per test with a divider between rows: the selector,
//     the test name, and its LOINC right-aligned ("—" when it has none);
//       - a tickable row (house panel) is a checkbox; a LOCKED row (CPT / lab panel) is the same
//         checkbox, checked and disabled;
//       - a LEFT-OUT row (a house-panel test unticked) is struck through and muted, with the amber
//         note "Left out. A package entitlement is still used for this panel." and an Undo;
//       - an ALREADY-ORDERED row has an icon, not a checkbox, and reads "Already ordered in X;
//         drawn once. Not added again.";
//       - a REFLEX row has an icon, not a checkbox, and reads "Added by the lab if <rule>. Billed
//         only then.";
//   · one hint line under the box (lock, info or layers icon);
//   · the house panel's amber "Bills as components" box when a test is left out.
//
// ICONS: src/encounter is lucide-free (PROD-1599), so every glyph is a DS one. Lock and Info are the
// design's own drawings (DS Icon wraps the same Lucide glyphs). The DS has no Layers or Repeat
// glyph: the already-ordered row uses DS `copy` and the reflex row ScripsIcon `recurring`, the
// nearest DS glyphs of the same meaning. A DS gap, flagged in the PR.
import { useId } from 'react';
import { Checkbox } from '@scripsteam/scrips-signal-ds';
import { Copy, Info, Lock, Repeat, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AP_FIELD_LABEL_CLASS } from './ap-dialog-label-style';
import { LEFT_OUT_NOTE, type LabTestViewState } from './lib/lab-catalogue';
import { useDesignV2 } from './lib/design-v2';

export interface LabTestsListRow {
  key: string;
  name: string;
  /** Absent → "—" (9.6). */
  loinc?: string;
  state: LabTestViewState;
  /** 'test' rows: ticked or not. */
  checked?: boolean;
  /** 'test' rows: checked-and-disabled, never toggles (CPT and lab panels, 6.10). */
  locked?: boolean;
  /** The reflex / already-ordered note. */
  note?: string;
}

export type LabTestsHintIcon = 'lock' | 'info' | 'layers';

export interface LabTestsListProps {
  header: string;
  rows: LabTestsListRow[];
  onToggle: (key: string) => void;
  hint?: { icon: LabTestsHintIcon; text: string } | null;
  /** A blocking message, e.g. "Select at least one test". */
  error?: string | null;
  /** The amber "Bills as components" box (7.8). */
  billing?: { cpts: string[]; text: string } | null;
  /**
   * The sentence on a left-out row (7.6, a house-panel rule). `null` = strike through and Undo
   * only: the legacy flow passes it because a legacy order has no kind, so the house-panel
   * sentence ("A package entitlement is still used…") cannot be known to hold.
   */
  leftOutNote?: string | null;
}

const LIST = 'mt-2 overflow-hidden rounded-md border border-solid border-border-subtle bg-surface-card';
/** Design sheet v1.1 (switch ON only): an inset list takes the card border, no lift. */
const LIST_V2 = 'mt-2 overflow-hidden rounded-md border border-solid border-border-card bg-surface-card';
const ROW = 'flex items-start gap-3 px-3 py-2 border-0 border-b border-solid border-b-border-subtle last:border-b-0';
const LINE = 'flex items-baseline justify-between gap-3';
const NAME = 'text-size-sm leading-reading text-text-primary';
const NAME_MUTED = 'text-size-sm leading-reading text-text-muted';
const NAME_LEFT_OUT = 'text-size-sm leading-reading text-text-muted line-through decoration-text-hint';
const LOINC = 'text-size-eyebrow text-text-hint tabular-nums whitespace-nowrap';
const NOTE = 'mt-1 text-size-eyebrow leading-body';
const GLYPH = 'inline-flex w-4 shrink-0 justify-center pt-1';
const UNDO =
  'inline-flex items-center border-0 bg-transparent p-0 font-sans text-size-eyebrow font-medium text-brand-primary cursor-pointer whitespace-nowrap';
const HINT = 'mt-2 flex items-center gap-2 text-size-eyebrow leading-body text-text-secondary';
const ERROR = 'm-0 mt-2 text-size-eyebrow leading-body text-status-error';
const BILLING =
  'mt-2 flex items-start gap-2 rounded-md bg-status-warning-bg px-3 py-2.5 text-size-eyebrow leading-body text-yellow-700';

const HINT_ICONS = { lock: Lock, info: Info, layers: Copy } satisfies Record<string, LucideIcon>;

function TestRow({
  row,
  onToggle,
  leftOutNote,
}: {
  row: LabTestsListRow;
  onToggle: (key: string) => void;
  leftOutNote: string | null;
}) {
  const id = useId();
  const loinc = row.loinc ?? '—';
  if (row.state !== 'test') {
    const already = row.state === 'already';
    return (
      <div className={ROW} data-state={row.state}>
        <span className={cn(GLYPH, already ? 'text-brand-depth' : 'text-text-hint')} aria-hidden="true">
          {already ? <Copy className="size-4" aria-hidden /> : <Repeat className="size-4" aria-hidden />}
        </span>
        <div className="grow min-w-0">
          <div className={LINE}>
            <span className={NAME_MUTED}>{row.name}</span>
            <span className={LOINC}>{loinc}</span>
          </div>
          {row.note && <div className={cn(NOTE, already ? 'text-brand-depth' : 'text-text-muted')}>{row.note}</div>}
        </div>
      </div>
    );
  }
  const leftOut = !row.locked && !row.checked;
  return (
    <div className={ROW} data-state={leftOut ? 'left-out' : row.locked ? 'locked' : 'test'}>
      <Checkbox
        id={id}
        aria-label={row.name}
        checked={!!row.checked}
        disabled={row.locked}
        onCheckedChange={() => {
          if (!row.locked) onToggle(row.key);
        }}
        // A locked row reads as ordered, not as unavailable: the DS dims a disabled control, the
        // design keeps the name at full strength.
        className={cn('mt-px', row.locked && '!opacity-100 !cursor-default')}
      />
      <div className="grow min-w-0">
        <div className={LINE}>
          <label htmlFor={id} className={cn(leftOut ? NAME_LEFT_OUT : NAME, !row.locked && 'cursor-pointer')}>
            {row.name}
          </label>
          <span className={LOINC}>{loinc}</span>
        </div>
        {leftOut && (
          <div className={cn(NOTE, 'text-status-amber-text')}>
            {leftOutNote ? `${leftOutNote} ` : null}
            <button type="button" className={UNDO} onClick={() => onToggle(row.key)}>
              Undo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function LabTestsList({
  header,
  rows,
  onToggle,
  hint,
  error,
  billing,
  leftOutNote = LEFT_OUT_NOTE,
}: LabTestsListProps) {
  const designV2 = useDesignV2();
  const HintGlyph = hint ? HINT_ICONS[hint.icon] : null;
  return (
    <div data-testid="lab-tests">
      <span className={AP_FIELD_LABEL_CLASS}>{header}</span>
      <div className={designV2 ? LIST_V2 : LIST} role="group" aria-label="Tests">
        {rows.map((r) => (
          <TestRow key={r.key} row={r} onToggle={onToggle} leftOutNote={leftOutNote} />
        ))}
      </div>
      {error ? (
        <p className={ERROR} data-testid="lab-tests-error">
          {error}
        </p>
      ) : (
        hint && (
          <div className={HINT} data-testid="lab-tests-hint">
            {HintGlyph && <HintGlyph className="size-4 shrink-0 text-text-secondary" aria-hidden />}
            {hint.text}
          </div>
        )
      )}
      {billing && (
        <div role="status" className={BILLING} data-testid="lab-tests-billing">
          <Info className="size-4 shrink-0" aria-hidden />
          <span>
            {billing.cpts.length > 0 && (
              <>
                <strong className="font-semibold">Bills as components:</strong> {billing.cpts.join(', ')}.{' '}
              </>
            )}
            {billing.text}
          </span>
        </div>
      )}
    </div>
  );
}
